import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import {
    Building2,
    FileText,
    ArrowRight,
    Play,
    MoreHorizontal,
    Eye,
    Edit,
    Trash2,
    CheckCircle2,
    Clock,
    XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { MaturityLevelBar } from '@/features/organizations/components/maturity-level-bar'
import { useMaturityColorMap } from '@/features/maturity-levels/maturity-color-utils'
import type { Organization } from '@/features/organizations/types'

// ── Types ──

interface MaturityOverviewOrg {
    id: string
    name: string
    industry_name?: string | null
    industry_id?: string | null
    description?: string | null
    size?: string | null
    created_by_name?: string | null
    industry_standard?: number | null
    latest_assessment?: {
        id: string
        name: string
        status: string
        overall_score?: number | null
        maturity_level?: number | null
        maturity_level_name?: string | null
        progress_percentage?: number | null
        updated_at?: string | null
    } | null
}

interface OrganizationCardProps {
    org: MaturityOverviewOrg
    /** Show the ⋯ menu with View/Edit/Delete actions */
    showActions?: boolean
    /** Stagger animation index */
    index?: number
    /** Callback when user clicks "Start Assessment" (no latest assessment) */
    onStartAssessment?: (orgId: string) => void
    /** Callback when user clicks Edit (requires showActions) */
    onEdit?: (org: Organization) => void
    /** Callback when user clicks Delete (requires showActions) */
    onDelete?: (org: Organization) => void
}

// ── Helpers ──

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
        completed: {
            label: 'Completed',
            icon: <CheckCircle2 className="h-3 w-3" />,
            className: 'bg-green-100 text-green-700',
        },
        in_progress: {
            label: 'In Progress',
            icon: <Clock className="h-3 w-3" />,
            className: 'bg-amber-100 text-amber-700',
        },
        draft: {
            label: 'Draft',
            icon: <FileText className="h-3 w-3" />,
            className: 'bg-gray-100 text-gray-700',
        },
        cancelled: {
            label: 'Cancelled',
            icon: <XCircle className="h-3 w-3" />,
            className: 'bg-red-100 text-red-700',
        },
    }
    const c = config[status] || config.draft
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${c.className}`}>
            {c.icon} {c.label}
        </span>
    )
}

// ── Actions Menu (for showActions mode) ──

function OrgCardMenu({
    org,
    onEdit,
    onDelete,
}: {
    org: MaturityOverviewOrg
    onEdit?: (org: Organization) => void
    onDelete?: (org: Organization) => void
}) {
    const [open, setOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    useEffect(() => {
        if (!open) return
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    return (
        <div className="relative" ref={menuRef}>
            <Button variant="ghost" size="icon" onClick={() => setOpen(!open)}>
                <MoreHorizontal className="h-4 w-4" />
            </Button>
            {open && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white border rounded-md shadow-lg z-50 py-1">
                    <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => { setOpen(false); navigate(`/organizations/${org.id}`) }}
                    >
                        <Eye className="h-4 w-4" /> View Details
                    </button>
                    <AuthorizationGate actions={['update_organization']}>
                        <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => { setOpen(false); onEdit?.(org as unknown as Organization) }}
                        >
                            <Edit className="h-4 w-4" /> Edit
                        </button>
                    </AuthorizationGate>
                    <AuthorizationGate actions={['delete_organization']}>
                        <hr className="my-1" />
                        <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                            onClick={() => { setOpen(false); onDelete?.(org as unknown as Organization) }}
                        >
                            <Trash2 className="h-4 w-4" /> Delete
                        </button>
                    </AuthorizationGate>
                </div>
            )}
        </div>
    )
}

// ── Main Card ──

export function OrganizationCard({
    org,
    showActions = false,
    index,
    onStartAssessment,
    onEdit,
    onDelete,
}: OrganizationCardProps) {
    const level = org.latest_assessment?.maturity_level ?? null
    const standard = org.industry_standard ?? null
    const { map: colorMap } = useMaturityColorMap()
    const levelColor = level != null ? (colorMap.get(level)?.color ?? null) : null

    let standardTag: { label: string; className: string } | null = null
    if (level != null && standard != null) {
        const diff = level - standard
        if (diff >= 1) {
            standardTag = { label: 'Above Standard', className: 'bg-green-100 text-green-700' }
        } else if (diff >= -1) {
            standardTag = { label: 'Meets Standard', className: 'bg-blue-100 text-blue-700' }
        } else {
            standardTag = { label: 'Below Standard', className: 'bg-red-100 text-red-700' }
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: (index ?? 0) * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
            whileHover={{ y: -2, transition: { duration: 0.2 } }}
        >
            <Card className="flex flex-col">
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <CardTitle className="text-sm font-semibold truncate">{org.name}</CardTitle>
                                {org.industry_name && (
                                    <CardDescription className="text-xs truncate">{org.industry_name}</CardDescription>
                                )}
                            </div>
                        </div>

                        {showActions ? (
                            <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" asChild>
                                    <Link to={`/organizations/${org.id}`}>
                                        View
                                        <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                    </Link>
                                </Button>
                                <OrgCardMenu org={org} onEdit={onEdit} onDelete={onDelete} />
                            </div>
                        ) : (
                            <Button variant="ghost" size="sm" asChild>
                                <Link to={`/organizations/${org.id}`}>
                                    View
                                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                </Link>
                            </Button>
                        )}
                    </div>
                </CardHeader>

                <CardContent className="pt-0 flex-1 flex flex-col justify-end space-y-3">
                    {/* Description */}
                    {org.description && (
                        <p className="text-xs text-gray-500 line-clamp-2">{org.description}</p>
                    )}
                    {/* Tags row */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {org.latest_assessment && (
                            <StatusBadge status={org.latest_assessment.status} />
                        )}
                        {standardTag && (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${standardTag.className}`}>
                                {standardTag.label}
                            </span>
                        )}
                    </div>

                    {level != null ? (
                        <div>
                            <MaturityLevelBar
                                level={level}
                                levelName={org.latest_assessment?.maturity_level_name}
                                color={levelColor}
                                industryStandard={standard}
                                showStandardTag={false}
                            />
                            {org.latest_assessment && org.latest_assessment.status === 'completed' && (
                                <Button variant="outline" size="sm" className="w-full mt-1" asChild>
                                    <Link to={`/assessments/${org.latest_assessment.id}/results`}>
                                        <FileText className="mr-1.5 h-3.5 w-3.5" />
                                        View Results
                                    </Link>
                                </Button>
                            )}
                            {org.latest_assessment && org.latest_assessment.status === 'in_progress' && (
                                <Button variant="outline" size="sm" className="w-full mt-1" asChild>
                                    <Link to={`/assessments/${org.latest_assessment.id}/questionnaire`}>
                                        <Play className="mr-1.5 h-3.5 w-3.5" />
                                        Continue Assessment
                                    </Link>
                                </Button>
                            )}
                        </div>
                    ) : !org.latest_assessment ? (
                        <AuthorizationGate actions={['create_assessment']}>
                            <div className="pt-1">
                                <Button
                                    size="sm"
                                    className="w-full"
                                    onClick={() => onStartAssessment?.(org.id)}
                                >
                                    <Play className="mr-1.5 h-3.5 w-3.5" />
                                    Start Assessment
                                </Button>
                            </div>
                        </AuthorizationGate>
                    ) : (
                        <p className="text-sm text-gray-400">No scores yet</p>
                    )}
                </CardContent>
                {org.created_by_name && (
                    <div className="px-6 pb-3 -mt-4">
                        <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-1.5">Created by {org.created_by_name}</p>
                    </div>
                )}
            </Card>
        </motion.div>
    )
}
